import { useCallback, useEffect, useRef } from "react";
import { AnswerGenerationStatus } from "../__generated__/graphql";

/**
 * How long the stream flag may claim a run without a fresh progress snapshot
 * before the claim is treated as the leftover of a dropped subscription.
 * Snapshots arrive every few seconds while a run is alive, so anything past
 * this is a dead stream, not a slow one.
 */
const STALL_MS = 20_000;
/** How often the watchdog re-checks staleness while a claim is standing. */
const TICK_MS = 5_000;

interface UseGenerationStreamRecoveryOptions {
  /** The app's "a progress stream is running right now" flag. */
  streamClaimsRunning: boolean;
  /** Latest server-side generation status for the message (null when unknown). */
  answerGenerationStatus: AnswerGenerationStatus | null;
  /**
   * A local mutation/regeneration is in flight. Recovery stays completely
   * quiet while true: the run is client-owned and its own flags will clear it.
   */
  suspended: boolean;
  /** Refetch the message so a fresh status and answer can land. */
  onRefetch: () => void;
  /** Drop the stream-running claim. */
  onClearStreamClaim: () => void;
}

interface UseGenerationStreamRecoveryReturn {
  /** Call from the progress subscription's onData on every snapshot. */
  noteStreamEvent: () => void;
}

/**
 * Recovers the answer-generation loading state when the progress subscription
 * dies mid-run, shared by Neptune and Venus so both surfaces recover on the
 * same rules.
 *
 * The stream-running flag's only off-switch is a terminal snapshot from the
 * very subscription that set it. When that subscription drops (Outlook
 * suspending the socket, a pod whose subscriber queues are gone when the
 * terminal broadcast fires), the flag pins the loading state forever over a
 * perfectly good stored answer. Two paths take it down:
 *
 * 1. An observed PENDING/PROCESSING -> terminal transition. The run this
 *    stream belonged to is over, so the claim is void; a snapshot from a newer
 *    run simply re-asserts it. This is the fast path and runs even while
 *    suspended, because the transition is proof, not a guess.
 * 2. A stale-stream watchdog. Once no snapshot has arrived for the stall
 *    window, the status has to settle the question — but the cached status
 *    lags a refetch behind the stream and can predate the run entirely, so it
 *    is only trusted after at least one fresh read in the current arming.
 *    While a fresh status still reports an active run the watchdog keeps
 *    refetching: with the subscription dead that is the only way the terminal
 *    status and the answer ever land. Owned PENDING matters here — a message
 *    can sit at PENDING for a while before the run actually starts, so
 *    PENDING counts as "running" and must not be read as a finished run.
 */
export function useGenerationStreamRecovery({
  streamClaimsRunning,
  answerGenerationStatus,
  suspended,
  onRefetch,
  onClearStreamClaim,
}: UseGenerationStreamRecoveryOptions): UseGenerationStreamRecoveryReturn {
  const lastEventAtRef = useRef(0);
  const noteStreamEvent = useCallback(() => {
    lastEventAtRef.current = Date.now();
  }, []);

  const serverReportsRunning =
    answerGenerationStatus === AnswerGenerationStatus.Pending ||
    answerGenerationStatus === AnswerGenerationStatus.Processing;

  // Fast path: the run the claim belongs to visibly ended.
  const prevStatusRef = useRef(answerGenerationStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = answerGenerationStatus;
    const wasRunning =
      prev === AnswerGenerationStatus.Pending ||
      prev === AnswerGenerationStatus.Processing;
    if (wasRunning && answerGenerationStatus !== null && !serverReportsRunning) {
      onClearStreamClaim();
    }
  }, [answerGenerationStatus, serverReportsRunning, onClearStreamClaim]);

  // Watchdog: armed only while a claim stands and nothing local backs it. A
  // status change re-arms it, which resets the fresh-read counter on purpose —
  // the new status still owes one read of its own before it is trusted.
  useEffect(() => {
    if (!streamClaimsRunning || suspended) return;
    let staleRefetches = 0;
    const tick = setInterval(() => {
      if (Date.now() - lastEventAtRef.current < STALL_MS) {
        staleRefetches = 0;
        return;
      }
      if (!serverReportsRunning && staleRefetches > 0) {
        onClearStreamClaim();
        return;
      }
      staleRefetches += 1;
      onRefetch();
    }, TICK_MS);
    return () => clearInterval(tick);
  }, [
    streamClaimsRunning,
    suspended,
    serverReportsRunning,
    onRefetch,
    onClearStreamClaim,
  ]);

  return { noteStreamEvent };
}
